export interface PhotonFeature {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    country?: string;
    countrycode?: string;
    district?: string;
    state?: string;
    // Photon responses also include an osm_id we can use for stable IDs
    osm_id?: number | string;
  };
}

export interface PhotonResponse {
  features: PhotonFeature[];
}

const DEFAULT_BIAS = {
  lat: 55.9533,
  lon: -3.1883
};

interface PhotonSearchOptions {
  signal?: AbortSignal;
  bias?: { lat: number; lon: number };
  preferredTerms?: string[];
  postcodePrefixes?: string[];
}

const scoreFeature = (feature: PhotonFeature, options: PhotonSearchOptions = {}): number => {
  const props = feature.properties ?? {};
  const normalize = (value?: string) => (value ? value.toLowerCase() : '');
  const haystack = [
    props.name,
    props.street,
    props.city,
    props.district,
    props.state,
    props.country,
    props.postcode
  ].map(value => normalize(value)).join(' ');
  const country = normalize(props.country) || normalize(props.countrycode);
  const state = normalize(props.state);
  const city = normalize(props.city);
  const district = normalize(props.district);
  const postcode = props.postcode ?? '';
  const preferredTerms = (options.preferredTerms?.length
    ? options.preferredTerms
    : ['edinburgh'])
    .map(term => normalize(term))
    .filter(Boolean);
  const postcodePrefixes = (options.postcodePrefixes?.length ? options.postcodePrefixes : ['EH'])
    .map(prefix => prefix.toUpperCase());

  let score = 0;
  if (postcodePrefixes.some(prefix => postcode.toUpperCase().startsWith(prefix))) score += 4;
  if (preferredTerms.some(term => haystack.includes(term))) score += 5;
  if (state.includes('scotland')) score += 3;
  if (country.includes('scotland')) score += 4;
  if (country.includes('united kingdom') || country === 'gb' || country === 'uk') score += 2;
  if (!country) score -= 1;
  return score;
};

export const searchPhoton = async (query: string, options: PhotonSearchOptions = {}): Promise<PhotonFeature[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const { signal, bias } = options;
  const { lat, lon } = bias ?? DEFAULT_BIAS;

  const params = new URLSearchParams({
    q: trimmed,
    limit: '8',
    lang: 'en'
  });
  if (typeof lat === 'number' && typeof lon === 'number') {
    params.set('lat', lat.toString());
    params.set('lon', lon.toString());
  }

  try {
    const res = await fetch(`https://photon.komoot.io/api/?${params.toString()}`, { signal });
    if (!res.ok) return [];
    const data: PhotonResponse = await res.json();
    const features = Array.isArray(data.features) ? data.features : [];
    return features
      .map(feature => ({ feature, score: scoreFeature(feature, options) }))
      .sort((a, b) => b.score - a.score)
      .map(entry => entry.feature);
  } catch (error) {
    if ((error as Error).name === 'AbortError') return [];
    console.warn('Photon geocoding failed', error);
    return [];
  }
};

export const formatPhotonLabel = (feature: PhotonFeature): string => {
  const { properties } = feature;
  const parts = [
    properties.housenumber,
    properties.street || properties.name,
    properties.district,
    properties.city,
    properties.postcode,
    properties.country
  ].filter(Boolean);
  const label = parts.join(' ').replace(/\s+/g, ' ').trim();
  return label || properties.name || 'Unknown location';
};
